import React, { useEffect, useMemo, useState } from 'react';
import {
  ABS_NOTE_BASE_LABEL,
  OUTCOME_LABEL,
  mergeServerScreen,
  deckPricePhrase,
  fmtMM,
  fmtNumMM,
  leaseLabel,
  noteCasePointer,
  packageDeckInputs,
  stripInputsFromAbsRun,
  screenDeal,
  valueCases,
  verdictHeadline,
  CASES_DIFFER_NOTE,
  caseNumbersForAssistant,
} from './model.js';
import { absScreenBody, deskAbsStructure, screenCached } from './absCache.js';
import { Loading, NextStep, SourceKey, useAssistantSummary, useMeeting, useServerScreen } from './common.jsx';
import { checklistForAssistant } from '../diligence/checklist.js';
import ReadinessChip from '../diligence/ReadinessChip.jsx';
import { useChecklist } from '../diligence/useChecklist.js';
import { isOilPrimary } from '../utils/commodityDisplay.js';

function riskTarget(risk) {
  const t = `${risk.title || ''} ${risk.summary || ''}`.toLowerCase();
  if (/midstream|differential|netback|gathering/.test(t)) return { key: 'land', label: 'Contracts' };
  if (/concentrat|boswell|single-well|decline/.test(t)) return { key: 'forecast', label: 'See forecast' };
  if (/aro|plug|liabilit/.test(t)) return { key: 'value', label: 'See value' };
  return { key: 'diligence', label: 'Request list' };
}

const STATE_LABEL = { fail: '✕ Fail', review: '! Review', pass: '✓ Pass' };

export default function Verdict({
  dealId,
  dealPackage,
  dealPackageFailed,
  diligence,
  executive,
  wells,
  land,
  map,
  geoscience,
  onNavigate,
}) {
  const { desk, forecast } = useMeeting({ dealId, dealPackage });
  const server = useServerScreen(dealId);
  const [unscaledNote, setUnscaledNote] = useState(null);
  const [financeStrip, setFinanceStrip] = useState(null);
  const screen = useMemo(
    () =>
      mergeServerScreen(
        screenDeal(dealPackage, { diligence, executive, aroMM: desk.aroMM }),
        dealPackage ? server?.result : null,
      ),
    [dealPackage, diligence, executive, desk.aroMM, server],
  );
  const cases = useMemo(
    () => (dealPackage ? valueCases(dealPackage, forecast, desk) : []),
    [dealPackage, forecast, desk],
  );
  const byId = Object.fromEntries(cases.map((c) => [c.id, c]));
  const facts = screen.facts;
  useEffect(() => {
    if (!dealPackage || !dealId) return undefined;
    let cancelled = false;
    screenCached(absScreenBody(dealId, deskAbsStructure(desk), desk))
      .then((result) => {
        if (cancelled) return;
        const head = result?.base?.summary?.headline || result?.base?.solve || null;
        setUnscaledNote(head?.maxNoteSizeMM ?? null);
        setFinanceStrip(stripInputsFromAbsRun(result?.base));
      })
      .catch(() => {
        if (!cancelled) {
          setUnscaledNote(null);
          setFinanceStrip(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, dealPackage, desk.aroMM, desk.aroSet, desk.bidPriceMM]);
  const packageDeck = packageDeckInputs(dealPackage);
  const packageDeckPhrase = deckPricePhrase(packageDeck);
  const analysisNoteMM = (server?.result?.criteria || []).find((c) => c.key === 'absNote')?.value ?? null;
  const unscaledPointer = noteCasePointer('analysis', unscaledNote, { strip: financeStrip });
  const { result: checklist } = useChecklist({
    dealId,
    pkg: dealPackage,
    diligence,
    wells,
    land,
    map,
    geoscience,
  });

  useAssistantSummary(
    facts
      ? {
          surface: 'analysis',
          bidPriceMM: desk.bidPriceMM,
          bidEdited: desk.bidEdited === true,
          noteLabel: ABS_NOTE_BASE_LABEL,
          noteCase: packageDeckPhrase
            ? `package deck, ${packageDeckPhrase}, unscaled 1PDP`
            : 'package deck, unscaled 1PDP',
          packageDeck,
          baseStrip: financeStrip,
          analysisNoteMM,
          financeNoteMM: unscaledNote,
          unscaledNotePointer: unscaledPointer,
          outcome: OUTCOME_LABEL[screen.outcome],
          checks: screen.checks.map((c) => ({ check: c.label, value: c.value, status: c.status })),
          pv10RangeMM: {
            conservative: byId.conservative?.pv10MM ?? null,
            base: byId.base?.pv10MM ?? null,
            aggressive: byId.aggressive?.pv10MM ?? null,
            sellerReportPdp: facts.refPdpPv10MM,
          },
          caseValues: caseNumbersForAssistant(Object.values(byId)),
          casesDiffer: CASES_DIFFER_NOTE,
          checklist: checklistForAssistant(checklist),
        }
      : null,
  );

  if (!dealPackage) {
    if (dealPackageFailed) {
      return (
        <div className="mp">
          <div className="mp-empty">
            No deal package is loaded for this deal on this host yet. Load the data room, then the
            screen runs automatically.
          </div>
        </div>
      );
    }
    return <Loading label="Screening the package…" />;
  }

  const risks = (diligence?.risks || []).slice(0, 4);
  const top = facts?.topAsset;
  const sub = [];
  if (facts?.topShare != null && top) {
    sub.push(
      `${leaseLabel(top.lease)} is ${Math.round(facts.topShare * 100)}% seller-basis share of PDP PV10${
        executive?.concentration?.boswellNetShare
          ? ` and ${Math.round(executive.concentration.boswellNetShare * 100)}% of ${
              isOilPrimary(dealPackage?.meta) || isOilPrimary(executive?.meta) ? 'net oil' : 'net gas'
            }`
          : ''
      }.`,
    );
  }
  if (risks.find((r) => /midstream/i.test(r.title))) {
    sub.push('Netbacks also depend on midstream contracts whose assignability is still open.');
  }
  if (facts?.modelVsSellerMM != null) {
    sub.push(
      `The rest of the package is long-lived${facts.hbpPct ? ', held by production' : ''}, and our rebuild reconciles to the seller's reserve report within ${fmtMM(Math.abs(facts.modelVsSellerMM), 2)}.`,
    );
  }

  return (
    <div className="mp">
      <section className="mp-card mp-verdict" aria-label="Analysis">
        <div className="mp-verdict-main">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={`mp-badge ${screen.outcome}`}>{OUTCOME_LABEL[screen.outcome].toUpperCase()}</span>
            <ReadinessChip
              pct={checklist?.readiness?.pre_offer?.pct}
              complete={checklist?.readiness?.pre_offer?.complete}
              partial={checklist?.readiness?.pre_offer?.partial}
              required={checklist?.readiness?.pre_offer?.required}
              overdue={checklist?.warnings?.overdue}
            />
            <span className="mp-note">
              vs. {screen.criteria.label} · {screen.failCount} fail · {screen.reviewCount} to review
              {screen.source === 'server' && screen.runAt ? ` · screened ${new Date(screen.runAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
            </span>
          </div>
          <h2>{verdictHeadline(screen)}</h2>
          <p className="mp-p" style={{ fontSize: 16 }}>
            {sub.join(' ')}
          </p>
        </div>
        <div className="mp-range">
          <div className="mp-note" style={{ fontWeight: 600, letterSpacing: '0.04em' }}>
            VALUE RANGE · PDP PV10, $MM
          </div>
          <div className="mp-range-grid">
            <div>
              <span className="k">Conservative</span>
              <span className="v">{fmtNumMM(byId.conservative?.pv10MM, 1)}</span>
            </div>
            <div>
              <span className="k">Our base</span>
              <span className="v">{fmtNumMM(byId.base?.pv10MM, 1)}</span>
            </div>
            <div>
              <span className="k">Aggressive</span>
              <span className="v">{fmtNumMM(byId.aggressive?.pv10MM, 1)}</span>
            </div>
          </div>
          <div className="mp-note" style={{ color: 'var(--mp-ink-2)' }}>
            Seller's reserve report <b className="mp-num">{fmtNumMM(facts?.refPdpPv10MM, 1)}</b>
            {desk.aroMM > 0 ? (
              <>
                {' '}
                · ARO <b className="mp-num">({desk.aroMM.toFixed(1)})</b>
              </>
            ) : null}
          </div>
          <div>
            <button type="button" className="mp-link" onClick={() => onNavigate('value')}>
              How the cases are built →
            </button>
          </div>
        </div>
      </section>

      <section className="mp-checks" aria-label="Screening checks">
        {screen.checks.map((c) => (
          <div key={c.id} className={`mp-checkcard ${c.status}`}>
            <div className="top">
              <span>{c.label}</span>
              <span className={`state ${c.status}`}>{STATE_LABEL[c.status]}</span>
            </div>
            <div className="v">{c.value}</div>
            <div className="s">{c.detail}</div>
            {c.id === 'absNote' ? (
              <div className="s" data-testid="note-case-pointer">
                {unscaledPointer}
              </div>
            ) : null}
          </div>
        ))}
      </section>

      <div className="mp-row">
        <section className="mp-card mp-grow-3" aria-label="What could kill it">
          <h3 className="mp-h3">What could kill it</h3>
          <div>
            {risks.length ? (
              risks.map((r) => {
                const target = riskTarget(r);
                const sev = String(r.severity || '').toLowerCase();
                return (
                  <div key={r.id || r.title} className="mp-list-item">
                    <span className={`mp-badge ${sev === 'high' ? 'high' : sev === 'medium' ? 'medium' : 'low'}`} style={{ minWidth: 52 }}>
                      {sev === 'high' ? 'HIGH' : sev === 'medium' ? 'MED' : 'LOW'}
                    </span>
                    <div className="body">
                      <div className="title">{r.title}</div>
                      <div className="text">{r.summary}</div>
                    </div>
                    <button type="button" className="mp-link" onClick={() => onNavigate(target.key)}>
                      {target.label} →
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="mp-note">No risk register loaded for this deal.</div>
            )}
          </div>
        </section>

        <div className="mp-grow-2" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <section className="mp-card" aria-label="What looks solid">
            <h3 className="mp-h3">What looks solid</h3>
            {facts?.modelVsSellerMM != null ? (
              <div className="mp-check">
                <span className="ok">✓</span>
                <span>
                  Our rebuilt model is within <b>{fmtMM(Math.abs(facts.modelVsSellerMM), 2)}</b> (
                  {Math.round((Math.abs(facts.modelVsSellerMM) / facts.refPdpPv10MM) * 100)}%) of the seller's PDP PV10.
                </span>
              </div>
            ) : null}
            {facts?.hbpPct != null ? (
              <div className="mp-check">
                <span className="ok">✓</span>
                <span>
                  <b>{(facts.hbpPct * 100).toFixed(1)}% held by production</b>
                  {facts.netAcres ? `, ${Math.round(facts.netAcres).toLocaleString()} net acres` : ''}.
                </span>
              </div>
            ) : null}
            {facts?.negativeWells ? (
              <div className="mp-check">
                <span className="ok">✓</span>
                <span>
                  Negative-PV wells stay in totals: <b>{facts.negativeWells} wells</b>, plug liability tracked separately.
                </span>
              </div>
            ) : null}
          </section>
          <NextStep
            label="2 · Forecast"
            question={
              top
                ? `Do we believe ${leaseLabel(top.lease)}'s forecast as much as the seller does?`
                : 'Do we believe the seller’s forecast?'
            }
            onClick={() => onNavigate('forecast')}
          />
        </div>
      </div>

      <SourceKey />
    </div>
  );
}
